import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import CTASection from '../../components/public/CTASection';
import { publicApi } from '../../services/api';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const orderMode = searchParams.get('order') === 'true';

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [colors, setColors] = useState({ primary: '#081626', secondary: '#00CFFF' });

  useEffect(() => {
    publicApi.getSettings()
      .then(res => {
        const groups = res.data.data || {};
        const flat = {};
        Object.values(groups).forEach(items => {
          items.forEach(item => { flat[item.key] = item.value; });
        });
        setColors({
          primary: flat.primary_color || '#081626',
          secondary: flat.secondary_color || '#00CFFF',
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    // Try to find the service from the home page sections
    publicApi.getPage('home')
      .then(res => {
        const sections = res.data.data.sections || [];
        const servicesSection = sections.find(s => s.type === 'services');
        if (servicesSection) {
          const content = typeof servicesSection.content === 'string'
            ? (() => { try { return JSON.parse(servicesSection.content); } catch { return {}; } })()
            : servicesSection.content;
          const services = content.services || [];
          const found = services.find(s => s.slug === slug);
          if (found) {
            setService(found);
          } else {
            setError('Layanan tidak ditemukan.');
          }
        } else {
          setError('Layanan tidak ditemukan.');
        }
      })
      .catch(() => setError('Gagal memuat data layanan.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-light">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#66E0FF', borderTopColor: 'transparent' }} />
          <p className="text-gray-500">Memuat...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-bg-light pt-20">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
            <span className="text-4xl mb-4 block">🔍</span>
            <p className="text-red-600 mb-4">{error || 'Layanan tidak ditemukan.'}</p>
            <Link to="/" className="inline-block px-6 py-2 rounded-full text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #00CFFF, #66E0FF)', color: '#081626' }}>
              Kembali ke Beranda
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Header */}
      <section className="pt-32 pb-20 text-white text-center" style={{ background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)` }}>
        <div className="max-w-4xl mx-auto px-4">
          {service.icon && <span className="text-6xl block mb-4">{service.icon}</span>}
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4" style={{ fontFamily: "'Cabin', sans-serif" }}>
            {service.name}
          </h1>
          {service.short_description && (
            <p className="text-lg" style={{ color: 'rgba(255,255,255,0.85)' }}>{service.short_description}</p>
          )}
          {service.price && (
            <div className="mt-6">
              <span className="text-3xl font-extrabold">{service.price}</span>
              {service.price_unit && <span className="text-sm ml-1" style={{ color: 'rgba(255,255,255,0.7)' }}>{service.price_unit}</span>}
            </div>
          )}
        </div>
      </section>

      {/* Two-column layout */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left: Details */}
            <div className="lg:col-span-3">
              {/* Description */}
              {service.description && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Cabin', sans-serif" }}>Deskripsi Layanan</h2>
                  <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed">
                    <p>{service.description}</p>
                  </div>
                </div>
              )}

              {/* Long description / detail */}
              {service.long_description && (
                <div className="mb-10" dangerouslySetInnerHTML={{ __html: service.long_description }} />
              )}

              {/* Features */}
              {(service.features || []).length > 0 && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cabin', sans-serif" }}>Yang Anda Dapatkan</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(service.features || []).map((feat, j) => (
                      <div key={j} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(0,207,255,0.05)' }}>
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#66E0FF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{typeof feat === 'string' ? feat : feat.label}</p>
                          {typeof feat !== 'string' && feat.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{feat.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ / Additional Info */}
              {(service.faq || []).length > 0 && (
                <div className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cabin', sans-serif" }}>Pertanyaan Umum</h2>
                  <div className="space-y-3">
                    {(service.faq || []).map((item, j) => (
                      <details key={j} className="bg-gray-50 rounded-xl p-4 group">
                        <summary className="font-medium text-gray-900 cursor-pointer list-none flex items-center justify-between">
                          {item.q}
                          <svg className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </summary>
                        <p className="text-sm text-gray-600 mt-3">{item.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Order Form */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {orderMode ? 'Pesan Sekarang' : 'Tertarik Dengan Layanan Ini?'}
                </h3>
                {submitted ? (
                  <div className="text-center py-8">
                    <span className="text-5xl block mb-4">✅</span>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Pesanan Terkirim!</h4>
                    <p className="text-sm text-gray-500">Tim kami akan menghubungi Anda dalam 1x24 jam untuk konfirmasi.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nama Lengkap</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:border-transparent outline-none"
                        style={{ '--tw-ring-color': '#66E0FF' }}
                        placeholder="Nama Anda"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:border-transparent outline-none"
                        style={{ '--tw-ring-color': '#66E0FF' }}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">No. Telepon</label>
                      <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:border-transparent outline-none"
                        style={{ '--tw-ring-color': '#66E0FF' }}
                        placeholder="0812-3456-7890"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Alamat</label>
                      <textarea
                        rows={2}
                        value={form.address}
                        onChange={e => setForm({ ...form, address: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:border-transparent outline-none resize-none"
                        style={{ '--tw-ring-color': '#66E0FF' }}
                        placeholder="Alamat lengkap"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Catatan (opsional)</label>
                      <textarea
                        rows={2}
                        value={form.message}
                        onChange={e => setForm({ ...form, message: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:border-transparent outline-none resize-none"
                        style={{ '--tw-ring-color': '#66E0FF' }}
                        placeholder="Tambahan informasi..."
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full font-semibold py-3 rounded-xl transition-all hover:shadow-lg text-sm"
                      style={{ background: 'linear-gradient(135deg, #00CFFF, #66E0FF)', color: '#081626' }}
                    >
                      Kirim Pesanan
                    </button>
                    <p className="text-center text-xs text-gray-400">
                      Tim kami akan merespons dalam 1x24 jam
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection content={{
        title: 'Butuh Bantuan Memilih Layanan?',
        subtitle: 'Tim ahli kami siap membantu Anda menemukan paket yang tepat',
        cta_text: 'Hubungi Kami',
        cta_link: '/contact',
      }} />

      <Footer />
    </div>
  );
}